import React, { useMemo, useState } from "react";
import AdminExport from "./AdminExport";
import "../../styles/adminListToolkit.css";

interface Column {
  key: string;
  label: string;
}

interface ExtraButton {
  label: string;
  onClick: () => void;
}

interface Props {
  title: string;
  columns: Column[];
  data: any[];
  selectedRow: any | null;

  onAdd?: () => void;
  onView?: (row: any) => void;
  onEdit?: (row: any) => void;
  onSoftDelete?: (row: any) => void;

  enableExport?: boolean;
  exportFileName?: string;

  extraButtons?: ExtraButton[];

  children: (filteredData: any[]) => React.ReactNode;
}

export default function AdminListToolkit({
  title,
  columns,
  data,
  selectedRow,
  onAdd,
  onView,
  onEdit,
  onSoftDelete,
  enableExport = false,
  exportFileName = "export",
  extraButtons = [],
  children,
}: Props) {
  const [searchTerm, setSearchTerm] = useState("");

  const requireSelection = (action?: (row: any) => void) => {
    if (!selectedRow) {
      alert("Please select a row first.");
      return;
    }
    action && action(selectedRow);
  };

  // 🔎 Global Search Filter
  const filteredData = useMemo(() => {
    if (!searchTerm) return data;

    return data.filter((row) =>
      columns.some((col) =>
        String(row[col.key] ?? "")
          .toLowerCase()
          .includes(searchTerm.toLowerCase()),
      ),
    );
  }, [searchTerm, data, columns]);

  return (
    <div className="admin-list-wrapper">
      {/* ================= TOOLBAR ================= */}
      <div className="admin-toolbar">
        <div className="toolbar-left">
          <h2>{title}</h2>
        </div>

        <div className="toolbar-right">
          {extraButtons.map((btn, index) => (
            <button key={index} className="btn-secondary" onClick={btn.onClick}>
              {btn.label}
            </button>
          ))}

          {onAdd && (
            <button className="btn-primary" onClick={onAdd}>
              Add
            </button>
          )}

          {onView && (
            <button
              className="btn-view"
              disabled={!selectedRow}
              onClick={() => requireSelection(onView)}
            >
              View
            </button>
          )}

          {onEdit && (
            <button
              className="btn-edit"
              disabled={!selectedRow}
              onClick={() => requireSelection(onEdit)}
            >
              Edit
            </button>
          )}

          {onSoftDelete && (
            <button
              className="btn-delete"
              disabled={!selectedRow}
              onClick={() => requireSelection(onSoftDelete)}
            >
              Delete
            </button>
          )}

          {/* 🔥 Export Integrcapated Here */}
          {enableExport && (
            <AdminExport
              fileName={exportFileName}
              data={filteredData}
              columns={columns}
            />
          )}
        </div>
      </div>

      {/* ================= GLOBAL SEARCH ================= */}
      <div className="admin-global-filter">
        <input
          type="text"
          placeholder="Search..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      {/* ================= TABLE RENDER ================= */}
      {children(filteredData)}
    </div>
  );
}
